/**
 * One dispatch: its state (`purchase_orders.read`), its document and events (`purchase_orders.dispatch`), and the actions that
 * permission allows (batch 8 T12c1, #3033). The state is what the server says now: when it cannot be refreshed the screen says that what
 * is shown may be out of date.
 *
 * Cancel and resolve are only offered when the state is known (the read permission), because offering them blindly would be guessing.
 * A cancel or resolve whose outcome is unknown is shown here as unknown, with the state just refreshed beside it, until the user
 * acknowledges it; it is not repeated on its own and the same action is refused until then.
 *
 * Every public entry point is its own boundary: the exported dialog drops what it holds (an open cancel or reconcile form, an answer it
 * showed, the document it loaded) on another tenant, user, session, rights, lock, purchase order, dispatch or opening, whether or not a
 * panel hosts it. Inside the panel the boundary adds nothing.
 */

import { useLayoutEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import {
  useDispatchAccess,
  useDispatchSummary,
  useUnconfirmedCommand,
  type Unconfirmed,
} from '@/hooks/useProcurementDispatch';
import { CancelDispatchDialog } from './CancelDispatchDialog';
import { DispatchHistory } from './DispatchHistory';
import { DispatchOwnedBoundary } from './DispatchOwnedBoundary';
import { useOpenSession } from './dispatchLifecycle';
import { DispatchStatusChip, DispatchStatusNote } from './DispatchStatus';
import { LoadingBlock, ReadError, Freshness } from './ReadFeedback';
import { ResolveDispatchDialog } from './ResolveDispatchDialog';
import type { DispatchCancelResult, DispatchResolveResult } from './dispatchTypes';

export interface DispatchDetailDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrderId: string;
  dispatchId: string;
  /** After a reconciliation as "not sent": the user chose to prepare a new dispatch (a new review and confirmation). */
  onPrepareNew?: (() => void) | undefined;
  /** A cancel or a "sent" reconciliation changed what the server says about the purchase order. */
  onPurchaseOrderChanged?: ((reason: 'cancelled' | 'resolved') => void) | undefined;
}

function UnknownOutcome({
  command,
  unconfirmed,
}: {
  command: 'cancel' | 'resolve';
  unconfirmed: Unconfirmed;
}) {
  const { t } = useTranslation('procurementDispatch');
  if (unconfirmed.mark === null) return null;
  return (
    <Alert
      severity="warning"
      role="alert"
      action={
        <Button color="inherit" size="small" onClick={unconfirmed.acknowledge}>
          {t('unknown.acknowledge')}
        </Button>
      }
    >
      <Typography variant="body2" fontWeight={600}>
        {t(`unknown.${command}Title`)}
      </Typography>
      <Typography variant="body2">{t('unknown.body')}</Typography>
    </Alert>
  );
}

export function DispatchDetailDialog(props: DispatchDetailDialogProps) {
  const session = useOpenSession(props.open);
  return (
    <DispatchOwnedBoundary>
      <DispatchDetailDialogBody
        key={`${props.purchaseOrderId}:${props.dispatchId}:${session}`}
        {...props}
      />
    </DispatchOwnedBoundary>
  );
}

function DispatchDetailDialogBody({
  open,
  onClose,
  purchaseOrderId,
  dispatchId,
  onPrepareNew,
  onPurchaseOrderChanged,
}: DispatchDetailDialogProps) {
  const { t } = useTranslation('procurementDispatch');
  const { t: tc } = useTranslation('common');
  const access = useDispatchAccess();
  const summaryRead = useDispatchSummary(purchaseOrderId, dispatchId);
  const cancelUnknown = useUnconfirmedCommand('cancel', dispatchId);
  const resolveUnknown = useUnconfirmedCommand('resolve', dispatchId);
  const [dialog, setDialog] = useState<'cancel' | 'resolve' | null>(null);
  const [cancelled, setCancelled] = useState<DispatchCancelResult | null>(null);
  const [resolved, setResolved] = useState<DispatchResolveResult | null>(null);

  // A closed dialog holds nothing: no open form, no earlier answer.
  useLayoutEffect(() => {
    if (open) return;
    setDialog(null);
    setCancelled(null);
    setResolved(null);
  }, [open]);

  const summary = summaryRead.data;
  const userId = access.owner?.userId ?? null;
  // Cancel is offered for every state that is neither finished nor already cancelled; the server decides what it means and may refuse.
  const canCancel =
    access.canDispatch &&
    summary !== undefined &&
    ['queued', 'sending', 'uncertain', 'sent'].includes(summary.status) &&
    summary.cancelRequestedAt === null;
  const canResolve = access.canDispatch && summary !== undefined && summary.status === 'uncertain';

  return (
    <>
      <ResponsiveDialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        mobileTitle={t('detail.title')}
        aria-label={t('detail.title')}
      >
        <DialogHeader title={t('detail.title')} onClose={onClose} />
        <DialogContent>
          <Stack spacing={2}>
            {access.canReadStatus ? (
              summaryRead.isLoading ? (
                <LoadingBlock label={t('detail.loading')} />
              ) : summary === undefined ? (
                <ReadError error={summaryRead.error} onRetry={summaryRead.refetch} />
              ) : (
                <Stack spacing={1.5} component="section" aria-label={t('detail.stateLabel')}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">{t('detail.state')}</Typography>
                    <DispatchStatusChip status={summary.status} />
                  </Box>
                  <Freshness
                    updatedAt={summaryRead.updatedAt}
                    isStale={summaryRead.isStale}
                    isRefetching={summaryRead.isRefetching}
                    error={summaryRead.error}
                    onRefresh={summaryRead.refetch}
                  />
                  <DispatchStatusNote summary={summary} currentUserId={userId} />
                </Stack>
              )
            ) : (
              <Alert severity="info">{t('detail.noStatusAccess')}</Alert>
            )}

            <UnknownOutcome command="cancel" unconfirmed={cancelUnknown} />
            <UnknownOutcome command="resolve" unconfirmed={resolveUnknown} />

            {cancelled !== null && cancelled.requestMayHaveLeft && (
              <Alert severity="warning" role="alert">
                <Typography variant="body2" fontWeight={600}>
                  {t('cancel.recordedOnly')}
                </Typography>
                <Typography variant="body2">{t('cancel.notPerformed')}</Typography>
              </Alert>
            )}
            {cancelled !== null && !cancelled.requestMayHaveLeft && (
              <Alert severity="success" role="status">
                {t('cancel.done')}
              </Alert>
            )}
            {resolved !== null && (
              <Alert severity="info" role="status">
                <Typography variant="body2">
                  {resolved.status === 'sent' ? t('resolve.doneSent') : t('resolve.doneNotSent')}
                </Typography>
                {resolved.nextStep !== null && (
                  <>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {t('resolve.nextStep')}
                    </Typography>
                    {onPrepareNew && (
                      <Button size="small" sx={{ mt: 1 }} onClick={onPrepareNew}>
                        {t('resolve.prepareNew')}
                      </Button>
                    )}
                  </>
                )}
              </Alert>
            )}

            {(canCancel || canResolve) && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {canResolve && (
                  <Button
                    variant="outlined"
                    onClick={() => setDialog('resolve')}
                    disabled={resolveUnknown.mark !== null}
                  >
                    {t('resolve.open')}
                  </Button>
                )}
                {canCancel && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setDialog('cancel')}
                    disabled={cancelUnknown.mark !== null}
                  >
                    {t('cancel.open')}
                  </Button>
                )}
              </Box>
            )}

            <Divider />
            {access.canDispatch ? (
              <DispatchHistory
                purchaseOrderId={purchaseOrderId}
                dispatchId={dispatchId}
                currentUserId={userId}
              />
            ) : (
              <Alert severity="info">{t('detail.noContentAccess')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{tc('close')}</Button>
        </DialogActions>
      </ResponsiveDialog>

      {summary !== undefined && dialog === 'cancel' && (
        <CancelDispatchDialog
          open
          summary={summary}
          onClose={() => setDialog(null)}
          onDone={(result) => {
            setCancelled(result);
            onPurchaseOrderChanged?.('cancelled');
          }}
        />
      )}
      {summary !== undefined && dialog === 'resolve' && (
        <ResolveDispatchDialog
          open
          summary={summary}
          onClose={() => setDialog(null)}
          onDone={(result) => {
            setResolved(result);
            if (result.status === 'sent') onPurchaseOrderChanged?.('resolved');
          }}
        />
      )}
    </>
  );
}
